import { cn } from '@dz-web/esboot-browser';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Popover, PopoverRef, Popup } from 'antd-mobile';
import { memo, useEffect, useMemo, useRef, useState } from 'react';

import { captureOrder, createOrder } from '@/api/mobile-shop/mutation';
import { tryGtag } from '@/helpers/gtags';
import staticConfig from '@/helpers/static-config';

import { PackageEnum } from '../../constants/enum';
import { ModalMessageBoxType, useModalMessageBox } from '../../hooks/use-modal-message-box';
import { usePackages } from '../../hooks/use-packages';
import IconClose from '../../images/close-icon.svg';

import styles from './buy-popup.scss';
import IconDropdown from './icon-drowndown.svg';

export interface BuyPopupProps {
  visible: boolean;
  onClose?: () => void;
  defaultPackageType: PackageEnum;
}

const initialOptions = {
  clientId: staticConfig.getPaypalClientId(),
  // "enable-funding": "venmo",
  'disable-funding': 'paylater,venmo', // 👈 添加这个来禁用 Pay Later
  // 'buyer-country': 'HK', // 生产打开这个会加载不了
  currency: 'HKD',
  'data-page-type': 'product-details',
  components: 'buttons',
  'data-sdk-integration-source': 'developer-studio',
  environment: (window.GLOBAL_CONFIG.mobileShop.paypal.isProd ? 'production' : 'sandbox') as 'production' | 'sandbox',
};

const areaCodesActions = ['852', '65', '886', '60', '853', '61', '1', '62', '64'].map((code) => ({
  key: code,
  text: code,
}));

export const BuyPopup = memo(({ visible, onClose, defaultPackageType }: BuyPopupProps) => {
  const [mobile, setMobile] = useState('');
  const { packages, packagesMap } = usePackages();
  const [activePackageType, setActivePackageType] = useState<PackageEnum>(() => defaultPackageType);
  const [areaCode, setAreaCode] = useState('852');
  const areaCodePopoverRef = useRef<PopoverRef>(null);
  const { showMessageBox } = useModalMessageBox();

  useEffect(() => {
    setActivePackageType(defaultPackageType);
  }, [defaultPackageType]);

  const activePackage = useMemo(() => packagesMap[activePackageType], [packages, activePackageType]);

  const handleMobileChange = (value: string) => {
    if (/^[0-9]*$/.test(value)) {
      setMobile(value);
    }
  };

  return (
    <Popup
      visible={visible}
      onMaskClick={onClose}
      bodyClassName={styles['buy-popup-body']}
      forceRender
      stopPropagation={[]}
    >
      <div>
        <div styleName="close-icon-container">
          <IconClose styleName="close-icon" onClick={onClose} />
        </div>
        <div styleName="select-package-tips">請選擇適合你的套餐</div>
        <div styleName="package-list">
          {packages.map(({ type, name }) => (
            <div
              styleName={cn('package-item', {
                active: type === activePackageType,
              })}
              key={type}
              onClick={() => setActivePackageType(type)}
            >
              {name}
            </div>
          ))}
        </div>
      </div>
      <div styleName="package-price">
        一個月合計: <span styleName="package-price-amount">{activePackage?.price || '--'}</span>
        <span styleName="package-price-unit">HKD</span>
      </div>
      <div>您的手機號:</div>
      <div styleName="mobile-input-container">
        <Popover
          destroyOnHide
          className={styles['area-code-popover']}
          ref={areaCodePopoverRef}
          content={
            <div styleName="area-code-popover-menu">
              {areaCodesActions.map(({ key, text }) => (
                <div
                  key={key}
                  onClick={() => {
                    setAreaCode(key);
                    areaCodePopoverRef.current?.hide();
                  }}
                  styleName={cn('area-code-popover-menu-item', {
                    active: areaCode === key,
                  })}
                >
                  {text}
                </div>
              ))}
            </div>
          }
          trigger="click"
          placement="bottom-start"
        >
          <div styleName="area">
            +{areaCode} <IconDropdown styleName="dropdown-icon" />
          </div>
        </Popover>
        <input
          type="number"
          styleName="mobile-input"
          placeholder="請輸入手機號碼"
          value={mobile}
          onChange={(e) => handleMobileChange(e.target.value)}
        />
      </div>
      <div styleName="quick-checkout-title">快速結帳選項</div>
      <div styleName="paypal-button-container">
        <PayPalScriptProvider options={initialOptions}>
          <PayPalButtons
            style={{
              shape: 'rect',
              layout: 'vertical',
              color: 'gold',
              label: 'paypal',
            }}
            onClick={async (_, actions) => {
              if (!activePackageType) {
                showMessageBox('請選擇套餐', ModalMessageBoxType.ERROR);
                return actions.reject();
              }

              if (!mobile) {
                showMessageBox('請輸入手機號碼', ModalMessageBoxType.ERROR);
                return actions.reject();
              }

              return actions.resolve();
            }}
            // eslint-disable-next-line consistent-return
            createOrder={async () => {
              try {
                // gtag 跟踪 创建订单
                tryGtag('event', 'conversion', {
                  send_to: window.GLOBAL_CONFIG.mobileShop.gtag.clickPaypalBuy,
                });

                if (!activePackageType) {
                  throw new Error('請選擇套餐');
                }

                if (!mobile) {
                  throw new Error('請輸入手機號碼');
                }

                const res = await createOrder({
                  payType: 'PAY_PAL',
                  packageName: activePackage.shortName,
                  packageCode: activePackage.code,
                  areaCode,
                  mobile,
                });

                return res.result.orderId;
              } catch (error: any) {
                showMessageBox(error?.message || error?.msg || '系統錯誤', ModalMessageBoxType.ERROR);
              }
            }}
            onApprove={async (data) => {
              try {
                await captureOrder({
                  orderId: data.orderID,
                  payType: 'PAY_PAL',
                });

                // gtag 跟踪 订单成功
                tryGtag('event', 'conversion', {
                  send_to: window.GLOBAL_CONFIG.mobileShop.gtag.sendTo,
                  transaction_id: data.orderID,
                });

                onClose?.();

                showMessageBox(
                  '您的服務已購買成功，系統將通過您的PayPal郵箱和手機短信通知您，請及時關注，注意將從當天開始計算服務時間，如有疑問請聯係客服，謝謝！',
                  ModalMessageBoxType.SUCCESS,
                );
              } catch (error: any) {
                console.error(error);
                showMessageBox(error?.message || error?.msg || '系統錯誤', ModalMessageBoxType.ERROR);
              }
            }}
          />
        </PayPalScriptProvider>
      </div>
      <div styleName="paytips">
        *订购成功后将获得一个月的付费版本使用时间，服务时间立即生效
        <div styleName="paytips-separator" />
        *賬號由系統自動生成，支付成功後，系統將自動發送賬號信息到您的PayPal郵箱和短信
      </div>
    </Popup>
  );
});
